import { AvailabilityQuickCreatePopover } from '@/components/availability/AvailabilityChangeEditors'
import { ActionSurface } from '@/components/patterns/action-surface'
import { useAvailabilityShortcutState } from '@/components/stores/StoreStatusControl'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { supportsAvailabilityChangesForOrganizationId } from '@/lib/services/availability-changes/availability-changes.query'
import { cn } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { ArrowRight, Ban, Flower2 } from 'lucide-react'
import { useState } from 'react'

export function AvailabilityQuickActionsCard({ organizationId, className = '' }) {
  const ability = useAbility()
  const path = usePathHelper()
  const availability = useAvailabilityShortcutState(organizationId)
  const [selection, setSelection] = useState(null)
  const hasActiveLimits = availability.currentRules.length > 0
  const canCreateChanges =
    supportsAvailabilityChangesForOrganizationId(organizationId) &&
    ability.can('create', 'availability-changes')
  const canUseQuickActions = Boolean(
    canCreateChanges &&
    availability.hasStore &&
    availability.isStoreActive &&
    availability.hasLocation
  )
  const title = hasActiveLimits
    ? 'Availability limits are active'
    : availability.statusLabel === 'Accepting orders'
      ? 'Accepting online orders'
      : availability.statusLabel
  const description = hasActiveLimits
    ? 'Review the limits currently affecting website orders.'
    : availability.nextUpcomingLabel
      ? `Next change: ${availability.nextUpcomingLabel}`
      : 'Pickup and delivery are available.'

  const openEditor = (key, initialEffects) => {
    const today = dayjs().format('YYYY-MM-DD')

    setSelection({
      key,
      startDate: today,
      endDate: today,
      mode: 'availability',
      presentation: 'sheet',
      initialEffects,
    })
  }

  return (
    <>
      <div className={cn('rounded-xl border border-border/70 bg-muted/25 p-4', className)}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'size-2.5 shrink-0 rounded-full',
                  hasActiveLimits ? 'bg-warning' : 'bg-success'
                )}
                aria-hidden="true"
              />
              <Typography variant="cardTitle" className="pb-0">
                {title}
              </Typography>
            </div>
            <Typography variant="caption" className="mt-1 block">
              {description}
            </Typography>
          </div>
          {availability.canListAvailability ? (
            <Link
              {...path.availability.index().link}
              className="shrink-0 text-sm font-semibold text-interaction hover:underline"
            >
              Manage
            </Link>
          ) : null}
        </div>

        {canCreateChanges ? (
          <div className="mt-4 grid gap-2">
            <ActionSurface
              padding="sm"
              className="group w-full items-center gap-3"
              disabled={!canUseQuickActions}
              onClick={() =>
                openEditor('pause', {
                  store_closed: false,
                  pickup_paused: true,
                  delivery_paused: true,
                  designers_choice_only: false,
                })
              }
            >
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-status-warning-background text-status-warning-foreground">
                <Ban className="size-4" />
              </span>
              <span className="min-w-0 flex-1">
                <Typography variant="labelStrong" className="block">
                  Pause pickup &amp; delivery
                </Typography>
                <Typography variant="caption" className="block">
                  Stop taking online orders
                </Typography>
              </span>
              <ArrowRight className="size-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-interaction" />
            </ActionSurface>

            <ActionSurface
              padding="sm"
              className="group w-full items-center gap-3"
              disabled={!canUseQuickActions || !availability.hasAvailableDesignersChoice}
              onClick={() =>
                openEditor('designers-choice', {
                  store_closed: false,
                  pickup_paused: false,
                  delivery_paused: false,
                  designers_choice_only: true,
                })
              }
            >
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-interaction">
                <Flower2 className="size-4" />
              </span>
              <span className="min-w-0 flex-1">
                <Typography variant="labelStrong" className="block">
                  Enable Designer&apos;s Choice only
                </Typography>
                <Typography variant="caption" className="block">
                  Limit web orders to Designer&apos;s Choice
                </Typography>
              </span>
              <ArrowRight className="size-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-interaction" />
            </ActionSurface>
          </div>
        ) : null}
      </div>

      <AvailabilityQuickCreatePopover
        key={selection ? `${selection.key}-${selection.startDate}` : 'closed'}
        selection={selection}
        open={Boolean(selection)}
        organizationId={organizationId}
        onClose={() => setSelection(null)}
        onClosed={() => setSelection(null)}
        onOpenChange={(open) => {
          if (!open) {
            setSelection(null)
          }
        }}
      />
    </>
  )
}
